/**
 * Auth0 PKCE flow — ported from `modules/legacy-client/src/utils/auth0Config.js`
 * (buildAuthorizeUrl, handleRedirectCallback, refreshAuth0Tokens, logout) per the
 * design spec Part C. Manual implementation (not `@auth0/auth0-spa-js`) because
 * single-spa clears window globals between mounts and the SDK doesn't persist its
 * own transaction state across that — sessionStorage does it explicitly instead.
 *
 * One deliberate difference from the legacy config: `redirectUri` is derived from
 * `window.location.origin` at call time (every tenant hostname is its own origin),
 * not a single hardcoded `https://metaship.ai`.
 */

export const AUTH0_DOMAIN = "auth.metaship.ai";
export const AUTH0_CLIENT_ID = "xaF96dvSV2CNn7ORTfzuG4lt5DjgY6C4";
export const AUTH0_SCOPE = "openid profile email offline_access";

const TRANSACTION_STORAGE_KEY = "auth0_transaction_state";

export interface Auth0User {
  sub: string;
  email: string;
  email_verified?: boolean;
  name?: string;
  given_name?: string;
  family_name?: string;
  picture?: string;
  [key: string]: unknown;
}

export interface Auth0CallbackResult {
  user: Auth0User;
  token: string;
  refreshToken: string | null;
}

/**
 * Auth0 explicitly rejected the refresh token (`invalid_grant` — revoked,
 * expired, or reuse-detected after a rotation race). Distinguished from a
 * generic network/5xx failure so callers know it's SAFE (and necessary) to
 * discard the stored refresh token and force a full re-login; any other
 * failure means the token is probably still good and the caller should retry.
 */
export class Auth0InvalidGrantError extends Error {
  constructor(message = "Auth0 refresh token is no longer valid") {
    super(message);
    this.name = "Auth0InvalidGrantError";
  }
}

interface TransactionState {
  state: string;
  codeVerifier: string;
  nonce: string;
}

function redirectUri(): string {
  return `${window.location.origin}/auth0/callback`;
}

function logoutRedirectUri(): string {
  return window.location.origin;
}

function generateRandomBase64UrlString(length = 43): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
}

function base64UrlEncode(buffer: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
}

async function sha256(plain: string): Promise<ArrayBuffer> {
  const data = new TextEncoder().encode(plain);
  return crypto.subtle.digest("SHA-256", data);
}

function saveTransactionState(state: TransactionState): void {
  sessionStorage.setItem(TRANSACTION_STORAGE_KEY, JSON.stringify(state));
}

function restoreTransactionState(): TransactionState | null {
  const raw = sessionStorage.getItem(TRANSACTION_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as TransactionState;
  } catch {
    return null;
  }
}

function clearTransactionState(): void {
  sessionStorage.removeItem(TRANSACTION_STORAGE_KEY);
}

async function buildAuthorizeUrl(options: { prompt?: string } = {}): Promise<string> {
  const state = generateRandomBase64UrlString(32);
  const codeVerifier = generateRandomBase64UrlString(43);
  const nonce = generateRandomBase64UrlString(32);
  const codeChallenge = base64UrlEncode(await sha256(codeVerifier));

  saveTransactionState({ state, codeVerifier, nonce });

  const params = new URLSearchParams({
    response_type: "code",
    client_id: AUTH0_CLIENT_ID,
    redirect_uri: redirectUri(),
    scope: AUTH0_SCOPE,
    state,
    nonce,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
    ...(options.prompt ? { prompt: options.prompt } : {}),
  });

  return `https://${AUTH0_DOMAIN}/authorize?${params.toString()}`;
}

/**
 * Redirect to Auth0 Universal Login. No `prompt` by default, so a user with a
 * live Auth0 SSO session signs in silently; pass `{ prompt: "select_account" }`
 * for an explicit "switch account" action.
 */
export async function loginWithRedirect(options: { prompt?: string } = {}): Promise<void> {
  const url = await buildAuthorizeUrl(options);
  window.location.assign(url);
}

/** Process the `/auth0/callback` redirect: verify state, exchange code, fetch userinfo. */
export async function handleRedirectCallback(
  search: string = window.location.search,
): Promise<Auth0CallbackResult> {
  try {
    const params = new URLSearchParams(search);

    // Auth0 itself can bounce the user straight back with an error (denied
    // consent, misconfigured connection, …) — that callback carries NO
    // `code`/`state` at all, so check for it FIRST and surface Auth0's own
    // description instead of the generic "no callback parameters" message.
    const authError = params.get("error");
    if (authError) {
      const description = params.get("error_description");
      throw new Error(description ? decodeURIComponent(description) : authError);
    }

    const code = params.get("code");
    const state = params.get("state");
    if (!code || !state) {
      throw new Error("No callback parameters found in URL");
    }

    const transaction = restoreTransactionState();
    if (!transaction) {
      throw new Error("No transaction state found in sessionStorage");
    }
    if (transaction.state !== state) {
      throw new Error("State mismatch — possible CSRF attack");
    }

    const tokenResponse = await fetch(`https://${AUTH0_DOMAIN}/oauth/token`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        grant_type: "authorization_code",
        client_id: AUTH0_CLIENT_ID,
        code,
        code_verifier: transaction.codeVerifier,
        redirect_uri: redirectUri(),
      }),
    });
    if (!tokenResponse.ok) {
      const detail = await tokenResponse.json().catch(() => ({}));
      throw new Error(`Token exchange failed: ${detail.error_description || detail.error || tokenResponse.status}`);
    }
    const tokens = await tokenResponse.json();

    const userInfoResponse = await fetch(`https://${AUTH0_DOMAIN}/userinfo`, {
      headers: { Authorization: `Bearer ${tokens.access_token}` },
    });
    if (!userInfoResponse.ok) {
      throw new Error("Failed to fetch user info");
    }
    const user = (await userInfoResponse.json()) as Auth0User;

    return {
      user,
      token: tokens.id_token as string,
      refreshToken: (tokens.refresh_token as string) || null,
    };
  } finally {
    // Always consumed — a retry after ANY failure (including an Auth0-side
    // error redirect) needs a fresh transaction, not a stale one.
    clearTransactionState();
  }
}

/**
 * Silently mint fresh Auth0 tokens from a stored (rotating) refresh token — no
 * UI, no redirect. Works even after the MetaShip access token has fully
 * expired, since the dead token never participates in its own renewal.
 */
export async function refreshAuth0Tokens(
  refreshToken: string,
): Promise<{ id_token: string | null; refresh_token: string | null }> {
  const res = await fetch(`https://${AUTH0_DOMAIN}/oauth/token`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      grant_type: "refresh_token",
      client_id: AUTH0_CLIENT_ID,
      refresh_token: refreshToken,
    }),
    signal: AbortSignal.timeout(10000),
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    // `invalid_grant` is Auth0's terminal verdict on THIS refresh token (dead,
    // revoked, or reuse-detected) — every other 4xx/5xx is presumed transient
    // (network blip, Auth0 hiccup) and must NOT be treated the same way.
    if (res.status >= 400 && res.status < 500 && detail.error === "invalid_grant") {
      throw new Auth0InvalidGrantError(detail.error_description || detail.error);
    }
    throw new Error(`Auth0 refresh failed: ${res.status} ${detail.error || ""}`);
  }
  const tokens = await res.json();
  return { id_token: tokens.id_token || null, refresh_token: tokens.refresh_token || null };
}

export function logout(): void {
  const url = `https://${AUTH0_DOMAIN}/v2/logout?${new URLSearchParams({
    client_id: AUTH0_CLIENT_ID,
    returnTo: logoutRedirectUri(),
  })}`;
  window.location.assign(url);
}
